<template>
  <div class="bg-white w-screen overflow-hidden">
    <!-- Breadcrumbs -->
    <BaseContainer>
      <section class="flex py-4 space-x-3">
        <NuxtLinkLocale to="/account">
          {{ $t('myAccount.account') }}
        </NuxtLinkLocale>
        <div>/</div>
        <NuxtLinkLocale to="/account/my-account">
          {{ $t('myAccount.myAccount') }}
        </NuxtLinkLocale>
        <div>/</div>
        <div>{{ $t('myAccount.loginInformation') }}</div>
      </section>
    </BaseContainer>

    <!-- Mobile: Header -->
    <MyAccountHeader
      :is-desktop="false"
      :detail="$t('myAccount.myAccountDetail')"
      to="/account/my-account"
    >
      <h1>
        <span class="text-primary-tangerine">
          {{ $t('myAccount.title.login') }}
        </span>
        {{ $t('myAccount.title.information3') }}
      </h1>
    </MyAccountHeader>

    <BaseContainer class="bg-gray-0">
      <section class="flex relative">
        <!-- Desktop: Side Navigation -->
        <AccountNavigation class="hidden lg:flex" />

        <!-- Content -->
        <main
          class="bg-gray-0 flex-1 relative before:absolute before:w-screen before:h-full before:top-0 before:left-0 before:pointer-events-none before:bg-gray-0"
        >
          <!-- Desktop: Header -->
          <MyAccountHeader
            is-desktop
            :detail="$t('myAccount.myAccountDetail')"
            to="/account/my-account"
          >
            <h1>
              <span class="text-primary-tangerine">
                {{ $t('myAccount.title.login') }}
              </span>
              {{ $t('myAccount.title.information3') }}
            </h1>
          </MyAccountHeader>

          <Form
            v-slot="{ errors, isSubmitting }"
            :validation-schema="schema"
            :initial-values="initialValues"
            @submit="onSubmit"
          >
            <section
              v-if="activePage === ''"
              class="my-10 ml-10 relative max-lg:ml-0 max-lg:my-8"
            >
              <div class="p-6 bg-white rounded-2xl">
                <div class="font-bold text-xl">
                  {{ $t('myAccount.title.change') }}
                  <span class="text-primary-tangerine">{{
                    $t('myAccount.title.mobilePhone')
                  }}</span>
                </div>
                <div class="mt-4">
                  <FormTextInput
                    :label="$t('myAccount.pleaseEnterYourNewMobilePhone')"
                    :placeholder="$t('myAccount.mobilePhone')"
                    name="mobile_phone"
                    type="tel"
                    :phone-selector="true"
                    full
                    only-number
                    :max-length="10"
                  />
                </div>
                <Button
                  :disabled="!isFormValid(errors)"
                  :loading="isSubmitting"
                  class="w-full"
                  >{{ $t('myAccount.sendOtpAndProceed') }}</Button
                >
              </div>
            </section>
            <section
              v-else
              class="my-10 ml-10 relative max-lg:ml-0 max-lg:my-8"
            >
              <div class="p-6 bg-white rounded-2xl">
                <div class="font-bold text-xl">
                  {{ $t('myAccount.title.change') }}
                  <span class="text-primary-tangerine">{{
                    $t('myAccount.title.mobilePhone')
                  }}</span>
                </div>
                <div class="mt-4 text-gray-50">
                  {{ $t('myAccount.receiveOtpForVerification') }}
                  <span class="font-bold">{{
                    formatPhone(data.mobile_phone)
                  }}</span>
                </div>
                <div
                  class="grid grid-cols-5 gap-x-4 mt-6 w-fit mx-auto max-lg:flex max-lg:w-full max-lg:justify-between"
                >
                  <div v-for="(_otp, index) in new Array(5)" :key="index">
                    <input
                      :id="`otp-input-field-${index}`"
                      v-model="otp[index]"
                      :class="
                        twJoin(
                          'bg-[#F6F5F4] w-[60px] h-[80px] max-lg:w-[50px] rounded-lg text-center text-2xl font-bold focus:border-2 focus:border-primary-tangerine outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                          otpError &&
                            'bg-error-red-10 border-[3px] border-error-red-20'
                        )
                      "
                      type="text"
                      :maxlength="5"
                      @input="(e) => handleInputOtp(e, index)"
                    />
                  </div>
                </div>
                <div
                  class="mt-10 grid grid-cols-2 max-lg:flex max-lg:flex-col-reverse max-lg:mt-4 gap-4"
                >
                  <div
                    :class="
                      twJoin(
                        'w-fit m-auto font-bold',
                        otpDelayed && 'text-gray-10',
                        !otpDelayed && 'cursor-pointer hover:opacity-50'
                      )
                    "
                    @click="handleRequestOtp"
                  >
                    {{ $t('myAccount.resendTheCode') }}
                    <span v-if="otpDelayed">(00:{{ 59 - counter }})</span>
                  </div>
                  <Button
                    type="button"
                    class="rounded-[8px] w-full"
                    @click="handleVerify"
                  >
                    {{ $t('myAccount.verifyProceed') }}
                  </Button>
                </div>
              </div>
            </section>
          </Form>
        </main>
      </section>
    </BaseContainer>
  </div>
</template>

<script setup lang="ts">
import { forEach } from 'lodash-es'
import { twJoin } from 'tailwind-merge'
import { userRepository } from '~/repository/userRepository'

const { access_token } = useUserStore()
const { t } = useI18n()
const router = useRouter()
const localePath = useLocalePath()
const route = useRoute()
const token = computed(() => route.query.token as string | undefined)
const otpError = ref(false)
const otp = ref(['', '', '', '', ''])
const otpDelayed = ref(false)
const activePage = ref('')
const data = reactive({
  country_mobile_phone: '',
  mobile_phone: '',
  ref_code: ''
})

const handleInputOtp = (e: Event, index: number) => {
  e.preventDefault()
  const event = e as InputEvent
  const target = event.target as HTMLInputElement

  if (!/^[0-9]*$/.test(target.value)) return

  otpError.value = false

  if (target.value.length === 5) {
    const lastEl = document.getElementById(
      `otp-input-field-4`
    ) as HTMLInputElement
    lastEl.focus()
    forEach(target.value?.slice(0, 5)?.split(''), (item, index2) => {
      otp.value[index2] = item
    })
    return
  } else {
    otp.value[index] = target.value.slice(0, 1)
  }

  const prevEl = document.getElementById(
    `otp-input-field-${index - 1}`
  ) as HTMLInputElement
  const nextEl = document.getElementById(
    `otp-input-field-${index + 1}`
  ) as HTMLInputElement

  if (event.inputType === 'deleteContentBackward') {
    otp.value[index] = ''
    if (prevEl) prevEl.focus()
  }
  if (event.inputType === 'insertText') {
    otp.value[index + 1] = ''
    if (nextEl) nextEl.focus()
  }
}

const schema = yup.object().shape({
  mobile_phone: yup
    .string()
    .phone(t('myAccount.inValidMobilePhone'))
    .required(t('myAccount.requiredMobilePhone'))
    .min(10, t('myAccount.inValidMobilePhone')),
  mobile_phone_country_code: yup.string()
})

const { counter, reset, pause, resume } = useInterval(1000, {
  controls: true,
  immediate: false
})

watch(
  () => counter.value,
  (nextCounter) => {
    if (nextCounter === 59) {
      pause()
      reset()
      otpDelayed.value = false
    }
  }
)

type Schema = InferType<typeof schema>

const { $api } = useNuxtApp()
const userRepo = userRepository($api)

const handleVerify = async () => {
  await userRepo
    .verifyChangePhoneNumberOtp({
      body: {
        ref_code: data.ref_code,
        otp_code: otp.value.join(''),
        phone_number: data.mobile_phone
      },
      onResponse: ({ response }) => {
        if (response.status === 200) {
          router.push(localePath('/account/my-account'))
        }
      }
    })
    .catch((error) => console.log(error))
}

const handleRequestOtp = async () => {
  if (counter.value == 0) {
    otpError.value = false
    otp.value = ['', '', '', '', '']

    await userRepo
      .requestChangePhoneNumberOtp(token.value, {
        body: {
          phone_number: data.mobile_phone
        },
        onResponse: ({ response }) => {
          if (response.status == 200) {
            resume()
            otpDelayed.value = true
          }
        }
      })
      .catch((error) => console.log(error))
  }
}

const initialValues = ref<Schema>({
  mobile_phone: '',
  mobile_phone_country_code: '+66'
})
const onSubmit = getSubmitFn(schema, async (values, actions) => {
  const phone_number =
    values.mobile_phone_country_code?.slice(1) +
    values.mobile_phone.slice(
      values.mobile_phone.length - 9,
      values.mobile_phone.length
    )
  data.country_mobile_phone = phone_number
  data.mobile_phone = values.mobile_phone
  await userRepo
    .requestChangePhoneNumberOtp(token.value, {
      body: {
        phone_number: data.mobile_phone
      },
      onResponse: ({ response }) => {
        if (response.status === 200) {
          activePage.value = 'otp'
          otpDelayed.value = true
          data.ref_code = response._data.data.refCode
          resume()
        }
      },
      onResponseError: ({ response }) => {
        if ([500, 400].includes(response.status)) {
          actions.setFieldError('mobile_phone', response._data.message)
        }
      }
    })
    .catch((error) => console.log(error))
})

onMounted(() => {
  if (!access_token) {
    router.push(localePath('sign-in'))
    return
  }
})
</script>

<style scoped></style>
